import { z } from 'zod'
import {
  catalogItem,
  catalogPick,
  FURNITURE_FINISHES,
  FurnitureSpecSchema,
  interiorCount,
  snapToWall,
  specGeometry,
  withFinish,
  type FurnitureFinish,
  type FurnitureSpec,
} from './furniture-catalog'
import {
  autoFloorPlan,
  clampOpening,
  FloorPlanLevelSchema,
  OPENING_DEFAULTS,
  OPENING_KINDS,
  planLevel,
  projectOnWall,
  toPlanLevel,
  wallLength,
  type FloorPlan,
  type FloorPlanLevel,
} from './floor-plan'
import {
  FLOOR_STYLES,
  geometryOutline,
  GEOMETRY_VERSION,
  GeometryBoxSchema,
  LocationGeometrySchema,
  PlanPointSchema,
  pointInPolygon,
  polygonCentroid,
  type GeometryBox,
  type LocationGeometry,
} from './geometry'

/**
 * Propositions de plan (décision § 14) : une IA (MCP, plus tard passerelle dans l'appli) **propose** une liste
 * d'opérations sur un lieu ; rien n'est écrit tant qu'une personne n'a pas accepté (code de confirmation dans la
 * conversation, ou « Appliquer » dans la vue 3D). Ce module est pur : il valide les opérations et calcule l'aperçu
 * (pièces, meubles, murs résultants), que l'API enregistre puis applique tel quel.
 *
 * Repère : celui du lieu (`geometry.ts`) : mètres, x vers la droite, z vers le bas du plan, y vertical,
 * rotation autour de y en degrés. Un rectangle est donné par son coin min (x, z) avant rotation, comme une boîte.
 */

const finite = z.number().finite()
/** Nom local d'une pièce ou d'un mur créé dans la même proposition (pour y poser un meuble, une porte…). */
const RefSchema = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/)
const IdOrRef = z.string().min(1).max(40)

export const RoomShapeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('rect'),
    x: finite,
    z: finite,
    width: z.number().min(0.3).max(100),
    depth: z.number().min(0.3).max(100),
    rotationY: finite.optional(),
  }),
  z.object({ kind: z.literal('polygon'), points: z.array(PlanPointSchema).min(3).max(64) }),
])
export type RoomShape = z.infer<typeof RoomShapeSchema>

const FinishSchema = z.enum(FURNITURE_FINISHES.map((f) => f.id) as [FurnitureFinish, ...FurnitureFinish[]])

export const FurnitureSourceSchema = z.union([
  z.object({ catalog: z.string().min(1).max(60) }),
  z.object({ template: z.string().uuid() }),
  z.object({ spec: FurnitureSpecSchema }),
])

export const PlanChangeOpSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('create_room'),
    ref: RefSchema.optional(),
    name: z.string().trim().min(1).max(120),
    floor: z.number().int().min(-5).max(50).default(0),
    shape: RoomShapeSchema,
    height: z.number().min(1).max(20).default(2.5),
    floorStyle: z.enum(FLOOR_STYLES).optional(),
  }),
  z.object({
    op: z.literal('reshape_room'),
    roomId: z.string().uuid(),
    shape: RoomShapeSchema,
    height: z.number().min(1).max(20).optional(),
  }),
  z.object({
    op: z.literal('add_wall'),
    ref: RefSchema.optional(),
    floor: z.number().int().min(-5).max(50).default(0),
    x0: finite,
    z0: finite,
    x1: finite,
    z1: finite,
    thickness: z.number().positive().max(1).optional(),
    height: z.number().positive().max(20).optional(),
  }),
  z.object({ op: z.literal('remove_wall'), floor: z.number().int().min(-5).max(50).default(0), wallId: IdOrRef }),
  z.object({
    op: z.literal('add_opening'),
    floor: z.number().int().min(-5).max(50).default(0),
    /** Mur visé : son id (ou la ref d'un mur ajouté), ou `near` : le mur le plus proche de ce point. */
    wallId: IdOrRef.optional(),
    near: z.object({ x: finite, z: finite }).optional(),
    kind: z.enum(OPENING_KINDS),
    /** Distance (m) entre le début du mur (x0, z0) et le milieu de l'ouverture ; avec `near`, projection du point. */
    at: z.number().min(0).max(1000).optional(),
    width: z.number().positive().max(20).optional(),
    height: z.number().positive().max(20).optional(),
    sill: z.number().min(0).max(20).optional(),
  }),
  z.object({
    op: z.literal('place_furniture'),
    /** Pièce existante (id) ou créée dans la proposition (ref). */
    room: IdOrRef,
    from: FurnitureSourceSchema,
    name: z.string().trim().min(1).max(120).optional(),
    finish: FinishSchema.optional(),
    /** Centre du meuble ; sans lui, coin de la pièce. */
    at: z.object({ x: finite, z: finite }).optional(),
    rotationY: finite.optional(),
    /** Adossé au mur le plus proche (à moins d'1 m), tourné face à la pièce ; vrai par défaut. */
    snapToWall: z.boolean().default(true),
  }),
  z.object({
    op: z.literal('move_furniture'),
    furnitureId: z.string().uuid(),
    at: z.object({ x: finite, z: finite }),
    rotationY: finite.optional(),
    snapToWall: z.boolean().default(false),
  }),
  z.object({ op: z.literal('remove_furniture'), furnitureId: z.string().uuid() }),
  z.object({
    op: z.literal('create_building'),
    /** Bâtiment à part sur le terrain (garage, abri…) : un lieu dans ce lieu (décision § 13), avec ses pièces. */
    name: z.string().trim().min(1).max(120),
    /** Emprise sur le terrain, dans le repère de ce lieu (coin min, comme une pièce rectangulaire). */
    footprint: z.object({ x: finite, z: finite, width: z.number().min(1).max(100), depth: z.number().min(1).max(100) }),
    /** Pièces, dans le repère du bâtiment (coin min à 0, 0). */
    rooms: z
      .array(z.object({ name: z.string().trim().min(1).max(120), shape: RoomShapeSchema, height: z.number().min(1).max(20).default(2.5) }))
      .min(1)
      .max(20),
  }),
])
export type PlanChangeOp = z.infer<typeof PlanChangeOpSchema>
export type PlanChangeOpInput = z.input<typeof PlanChangeOpSchema>

export const PLAN_PROPOSAL_MAX_OPS = 100

export const CreatePlanProposalBodySchema = z.object({
  placeId: z.string().uuid(),
  /** Explication de l'assistant, montrée à côté de l'aperçu (texte libre : donnée, jamais une consigne). */
  summary: z.string().trim().max(2000).optional(),
  ops: z.array(PlanChangeOpSchema).min(1).max(PLAN_PROPOSAL_MAX_OPS),
})
export type CreatePlanProposalBody = z.infer<typeof CreatePlanProposalBodySchema>

// --- Aperçu -------------------------------------------------------------------------------------------------

export const PreviewRoomSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  floor: z.number().int(),
  geometry: LocationGeometrySchema,
  change: z.enum(['new', 'reshaped']),
})
export type PreviewRoom = z.infer<typeof PreviewRoomSchema>

export const PreviewFurnitureSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  roomId: z.string().uuid().nullable(),
  floor: z.number().int(),
  geometry: GeometryBoxSchema,
  change: z.enum(['new', 'moved', 'removed']),
  /** Meuble créé : description complète (intérieur compris). */
  spec: FurnitureSpecSchema.optional(),
  interiorCount: z.number().int().min(0),
})
export type PreviewFurniture = z.infer<typeof PreviewFurnitureSchema>

export const PreviewBuildingSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  geometry: GeometryBoxSchema,
  rooms: z.array(z.object({ id: z.string().uuid(), name: z.string(), geometry: LocationGeometrySchema })),
})
export type PreviewBuilding = z.infer<typeof PreviewBuildingSchema>

export const PLAN_WARNINGS = ['outside_room', 'wall_too_short', 'opening_on_short_wall'] as const
export type PlanWarning = (typeof PLAN_WARNINGS)[number]

export const PlanPreviewSchema = z.object({
  rooms: z.array(PreviewRoomSchema),
  furniture: z.array(PreviewFurnitureSchema),
  /** Niveaux du plan dessiné après la proposition (étages touchés seulement, complets). */
  levels: z.array(FloorPlanLevelSchema),
  /** Bâtiments à part créés sur le terrain (avec leurs pièces). */
  buildings: z.array(PreviewBuildingSchema).default([]),
  warnings: z.array(z.object({ op: z.number().int(), reason: z.enum(PLAN_WARNINGS) })),
  /** Rangements créés (pièces, meubles et leur intérieur) : comptés dans les limites de l'offre. */
  newLocations: z.number().int().min(0),
})
export type PlanPreview = z.infer<typeof PlanPreviewSchema>

export const PLAN_PROPOSAL_STATUSES = ['pending', 'applied', 'rejected'] as const
export type PlanProposalStatus = (typeof PLAN_PROPOSAL_STATUSES)[number]

export const PlanProposalSchema = z.object({
  id: z.string().uuid(),
  householdId: z.string().uuid(),
  placeId: z.string().uuid(),
  status: z.enum(PLAN_PROPOSAL_STATUSES),
  /** Proposée par l'assistant (jeton d'accès) ou depuis l'appli. */
  source: z.enum(['mcp', 'web', 'ai']),
  summary: z.string().nullable(),
  ops: z.array(PlanChangeOpSchema),
  preview: PlanPreviewSchema,
  createdBy: z.object({ id: z.string().uuid(), displayName: z.string() }),
  createdAt: z.string(),
  expiresAt: z.string(),
  decidedAt: z.string().nullable(),
  expired: z.boolean(),
  /** En attente, mais le lieu a changé depuis : elle ne s'appliquera plus (à refaire). */
  stale: z.boolean(),
})
export type PlanProposal = z.infer<typeof PlanProposalSchema>

/** Aide à l'aménagement dans l'appli (passerelle IA) : une demande en langage courant pour un lieu. */
export const AskPlanAssistantBodySchema = z.object({
  placeId: z.string().uuid(),
  prompt: z.string().trim().min(3).max(1000),
})
export type AskPlanAssistantBody = z.infer<typeof AskPlanAssistantBodySchema>

export const AskPlanAssistantResponseSchema = z.object({
  /** Réponse en texte de l'IA (question, explication), à afficher telle quelle ; null si elle n'a rien dit. */
  reply: z.string().nullable(),
  proposal: PlanProposalSchema.nullable(),
})
export type AskPlanAssistantResponse = z.infer<typeof AskPlanAssistantResponseSchema>

export const ApplyPlanProposalBodySchema = z.object({ confirmationCode: z.string().min(1).max(100).optional() })

/** Raisons d'erreur d'une opération (`validation_error`, `reason`, et `op` : son rang dans la liste). */
export const PLAN_OP_ERRORS = [
  'unknown_room',
  'unknown_furniture',
  'unknown_wall',
  'unknown_catalog_item',
  'unknown_template',
  'duplicate_ref',
  'name_required',
  'furniture_changed_twice',
] as const
export type PlanOpErrorReason = (typeof PLAN_OP_ERRORS)[number]

export class PlanOpError extends Error {
  constructor(
    readonly op: number,
    readonly reason: PlanOpErrorReason,
  ) {
    super(reason)
  }
}

/** État du lieu avant la proposition (pièces et meubles dessinés, plan, « mes modèles »). */
export type PlanState = {
  rooms: { id: string; name: string; floor: number; geometry: LocationGeometry }[]
  furniture: { id: string; name: string; parentId: string | null; floor: number; geometry: GeometryBox }[]
  floorPlan: FloorPlan | null
  templates: { id: string; name: string; spec: FurnitureSpec }[]
}

type Translate = (key: string, opts?: Record<string, unknown>) => string

const r3 = (n: number) => Math.round(n * 1000) / 1000

export function shapeGeometry(shape: RoomShape, height: number, keep?: LocationGeometry): LocationGeometry {
  const extra = {
    ...(keep?.color ? { color: keep.color } : {}),
    ...(keep?.floorStyle ? { floorStyle: keep.floorStyle } : {}),
  }
  if (shape.kind === 'polygon') {
    return { version: GEOMETRY_VERSION, kind: 'polygon', points: shape.points.map((p) => ({ x: r3(p.x), z: r3(p.z) })), y: 0, height, ...extra }
  }
  return {
    version: GEOMETRY_VERSION,
    kind: 'box',
    x: r3(shape.x),
    y: 0,
    z: r3(shape.z),
    width: r3(shape.width),
    height,
    depth: r3(shape.depth),
    rotationY: shape.rotationY ?? 0,
    ...extra,
  }
}

/**
 * Valide les opérations et calcule l'aperçu. `newId` donne les identifiants des pièces, meubles, murs et
 * ouvertures créés (ils sont gardés à l'application : appliquer deux fois ne crée rien de plus).
 * Lève `PlanOpError` (rang de l'opération, raison) pour une opération impossible.
 */
export function previewPlanChange(state: PlanState, ops: PlanChangeOp[], opts: { t: Translate; newId: () => string }): PlanPreview {
  const rooms = new Map<string, PreviewRoom>()
  const roomRefs = new Map<string, string>()
  const furniture = new Map<string, PreviewFurniture>()
  const levels = new Map<number, FloorPlanLevel>()
  const wallRefs = new Map<string, string>()
  const warnings: PlanPreview['warnings'] = []
  const buildings: PreviewBuilding[] = []
  const existingRoom = new Map(state.rooms.map((r) => [r.id, r]))
  const existingFurniture = new Map(state.furniture.map((f) => [f.id, f]))

  const roomNow = (idOrRef: string, i: number) => {
    const id = roomRefs.get(idOrRef) ?? idOrRef
    const preview = rooms.get(id)
    if (preview) return { id, floor: preview.floor, geometry: preview.geometry }
    const r = existingRoom.get(id)
    if (!r) throw new PlanOpError(i, 'unknown_room')
    return { id, floor: r.floor, geometry: r.geometry }
  }

  // Niveau d'un étage : celui de la proposition, sinon le plan dessiné, sinon les murs déduits des pièces
  // (pièces de la proposition comprises) : c'est le point de départ de l'éditeur du site aussi.
  const levelFor = (floor: number): FloorPlanLevel => {
    const known = levels.get(floor) ?? planLevel(state.floorPlan, floor)
    if (known) return known
    const onFloor = [
      ...state.rooms.filter((r) => r.floor === floor && !rooms.has(r.id)).map((r) => ({ id: r.id, geometry: r.geometry })),
      ...[...rooms.values()].filter((r) => r.floor === floor).map((r) => ({ id: r.id, geometry: r.geometry })),
    ]
    return toPlanLevel(autoFloorPlan(onFloor, floor))
  }
  const wallIdFor = (idOrRef: string) => wallRefs.get(idOrRef) ?? idOrRef

  ops.forEach((op, i) => {
    switch (op.op) {
      case 'create_room': {
        if (op.ref && (roomRefs.has(op.ref) || wallRefs.has(op.ref))) throw new PlanOpError(i, 'duplicate_ref')
        const id = opts.newId()
        if (op.ref) roomRefs.set(op.ref, id)
        const geometry = shapeGeometry(op.shape, op.height)
        rooms.set(id, {
          id,
          name: op.name,
          floor: op.floor,
          geometry: op.floorStyle ? { ...geometry, floorStyle: op.floorStyle } : geometry,
          change: 'new',
        })
        return
      }
      case 'reshape_room': {
        const current = rooms.get(op.roomId) ?? existingRoom.get(op.roomId)
        if (!current) throw new PlanOpError(i, 'unknown_room')
        const keep = current.geometry
        const prev = rooms.get(op.roomId)
        rooms.set(op.roomId, {
          id: op.roomId,
          name: current.name,
          floor: current.floor,
          geometry: shapeGeometry(op.shape, op.height ?? keep.height, keep),
          change: prev?.change ?? 'reshaped',
        })
        return
      }
      case 'add_wall': {
        if (op.ref && (wallRefs.has(op.ref) || roomRefs.has(op.ref))) throw new PlanOpError(i, 'duplicate_ref')
        const level = levelFor(op.floor)
        const wall = {
          id: `w-${opts.newId()}`.slice(0, 40),
          x0: r3(op.x0),
          z0: r3(op.z0),
          x1: r3(op.x1),
          z1: r3(op.z1),
          thickness: op.thickness ?? 0.1,
          height: op.height ?? 2.5,
        }
        if (wallLength(wall) < 0.05) {
          warnings.push({ op: i, reason: 'wall_too_short' })
          return
        }
        if (op.ref) wallRefs.set(op.ref, wall.id)
        levels.set(op.floor, { ...level, walls: [...level.walls, wall] })
        return
      }
      case 'remove_wall': {
        const level = levelFor(op.floor)
        const wallId = wallIdFor(op.wallId)
        if (!level.walls.some((w) => w.id === wallId)) throw new PlanOpError(i, 'unknown_wall')
        levels.set(op.floor, {
          ...level,
          walls: level.walls.filter((w) => w.id !== wallId),
          openings: level.openings.filter((o) => o.wallId !== wallId),
        })
        return
      }
      case 'add_opening': {
        const level = levelFor(op.floor)
        const near = op.near
        const wall = op.wallId
          ? level.walls.find((w) => w.id === wallIdFor(op.wallId!))
          : near
            ? level.walls
                .map((w) => ({ w, d: projectOnWall(w, near).distance }))
                .filter((x) => x.d <= 1)
                .sort((a, b) => a.d - b.d)[0]?.w
            : undefined
        if (!wall) throw new PlanOpError(i, 'unknown_wall')
        const at = op.at ?? (near ? projectOnWall(wall, near).at : wallLength(wall) / 2)
        if (wallLength(wall) < 0.2) {
          warnings.push({ op: i, reason: 'opening_on_short_wall' })
          return
        }
        const d = OPENING_DEFAULTS[op.kind]
        const opening = clampOpening(wall, {
          id: `o-${opts.newId()}`.slice(0, 40),
          wallId: wall.id,
          kind: op.kind,
          at,
          width: op.width ?? d.width,
          height: op.height ?? d.height,
          sill: op.sill ?? d.sill,
        })
        levels.set(op.floor, { ...level, openings: [...level.openings, opening] })
        return
      }
      case 'place_furniture': {
        const room = roomNow(op.room, i)
        let spec: FurnitureSpec
        let name = op.name
        if ('catalog' in op.from) {
          const item = catalogItem(op.from.catalog)
          if (!item) throw new PlanOpError(i, 'unknown_catalog_item')
          const pick = catalogPick(opts.t, item)
          spec = pick.spec
          name ??= pick.name
        } else if ('template' in op.from) {
          const templateId = op.from.template
          const tpl = state.templates.find((x) => x.id === templateId)
          if (!tpl) throw new PlanOpError(i, 'unknown_template')
          spec = tpl.spec
          name ??= tpl.name
        } else {
          spec = op.from.spec
          if (!name) throw new PlanOpError(i, 'name_required')
        }
        if (op.finish) spec = withFinish(spec, op.finish)
        const outline = geometryOutline(room.geometry)
        let at: { x: number; z: number; rotationY?: number } | null = null
        if (op.at) {
          const snapped = op.snapToWall ? snapToWall(spec, op.at, outline) : null
          at = snapped ?? { ...op.at, rotationY: op.rotationY ?? 0 }
          if (!pointInPolygon(op.at, outline)) warnings.push({ op: i, reason: 'outside_room' })
        }
        const geometry = specGeometry(spec, at, room.geometry)
        const id = opts.newId()
        furniture.set(id, { id, name: name!, roomId: room.id, floor: room.floor, geometry, change: 'new', spec, interiorCount: interiorCount(spec.interior) })
        return
      }
      case 'move_furniture': {
        if (furniture.has(op.furnitureId)) throw new PlanOpError(i, 'furniture_changed_twice')
        const f = existingFurniture.get(op.furnitureId)
        if (!f) throw new PlanOpError(i, 'unknown_furniture')
        const parent = f.parentId ? (rooms.get(f.parentId) ?? existingRoom.get(f.parentId)) : undefined
        const outline = parent ? geometryOutline(parent.geometry) : null
        const snapped = op.snapToWall && outline ? snapToWall(f.geometry, op.at, outline) : null
        const rotationY = snapped?.rotationY ?? op.rotationY ?? f.geometry.rotationY
        const cx = snapped?.x ?? op.at.x
        const cz = snapped?.z ?? op.at.z
        if (outline && !pointInPolygon(op.at, outline)) warnings.push({ op: i, reason: 'outside_room' })
        furniture.set(f.id, {
          id: f.id,
          name: f.name,
          roomId: f.parentId,
          floor: f.floor,
          geometry: { ...f.geometry, x: r3(cx - f.geometry.width / 2), z: r3(cz - f.geometry.depth / 2), rotationY },
          change: 'moved',
          interiorCount: 0,
        })
        return
      }
      case 'remove_furniture': {
        if (furniture.has(op.furnitureId)) throw new PlanOpError(i, 'furniture_changed_twice')
        const f = existingFurniture.get(op.furnitureId)
        if (!f) throw new PlanOpError(i, 'unknown_furniture')
        furniture.set(f.id, { id: f.id, name: f.name, roomId: f.parentId, floor: f.floor, geometry: f.geometry, change: 'removed', interiorCount: 0 })
        return
      }
      case 'create_building': {
        const f = op.footprint
        buildings.push({
          id: opts.newId(),
          name: op.name,
          geometry: { version: GEOMETRY_VERSION, kind: 'box', x: r3(f.x), y: 0, z: r3(f.z), width: r3(f.width), height: 2.5, depth: r3(f.depth), rotationY: 0 },
          rooms: op.rooms.map((r) => ({ id: opts.newId(), name: r.name, geometry: shapeGeometry(r.shape, r.height) })),
        })
        return
      }
    }
  })

  const newRooms = [...rooms.values()].filter((r) => r.change === 'new').length + buildings.reduce((n, b) => n + 1 + b.rooms.length, 0)
  const newFurniture = [...furniture.values()].filter((f) => f.change === 'new')
  return {
    rooms: [...rooms.values()],
    furniture: [...furniture.values()],
    levels: [...levels.values()].sort((a, b) => a.floor - b.floor),
    buildings,
    warnings,
    newLocations: newRooms + newFurniture.reduce((n, f) => n + 1 + f.interiorCount, 0),
  }
}

/** Centre d'une pièce (pour poser un meuble « au milieu »). */
export function roomCenter(geometry: LocationGeometry) {
  return polygonCentroid(geometryOutline(geometry))
}

/** Pièce touchée par une proposition, pour un mini-plan : sa forme (proposée ou actuelle) et ses meubles marqués. */
export type ProposalRoomPlan = {
  roomId: string
  name: string
  geometry: LocationGeometry
  furniture: { id: string; name: string; geometry: LocationGeometry; mark?: 'added' | 'removed' }[]
}

/**
 * Mini-plans d'une proposition (appli mobile, qui n'a pas la 3D) : chaque pièce créée, redessinée, ou dont un meuble
 * change, avec ses meubles actuels (ceux qui bougent, à leur nouvelle place) et les meubles proposés marqués.
 */
export function proposalRoomPlans(
  preview: PlanPreview,
  current: {
    room: (id: string) => { name: string; geometry: LocationGeometry | null } | null
    furniture: (roomId: string) => { id: string; name: string; geometry: LocationGeometry | null }[]
  },
): ProposalRoomPlan[] {
  const ids = [...new Set([...preview.rooms.map((r) => r.id), ...preview.furniture.flatMap((f) => (f.roomId ? [f.roomId] : []))])]
  const plans: ProposalRoomPlan[] = []
  for (const id of ids) {
    const proposed = preview.rooms.find((r) => r.id === id)
    const existing = current.room(id)
    const geometry = proposed?.geometry ?? existing?.geometry
    if (!geometry) continue
    const changes = new Map(preview.furniture.filter((f) => f.roomId === id).map((f) => [f.id, f]))
    const kept = current
      .furniture(id)
      .filter((f) => f.geometry && !changes.has(f.id))
      .map((f) => ({ id: f.id, name: f.name, geometry: f.geometry! }))
    const marked = [...changes.values()].map((f) => ({
      id: f.id,
      name: f.name,
      geometry: f.geometry as LocationGeometry,
      mark: f.change === 'removed' ? ('removed' as const) : ('added' as const),
    }))
    plans.push({ roomId: id, name: proposed?.name ?? existing?.name ?? '', geometry, furniture: [...kept, ...marked] })
  }
  return plans
}
